import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type InputHTMLAttributes } from 'react';

import { cn } from '../lib/cn';

/**
 * Input do Trakd · padrão único.
 *
 * Variants:
 *  - default — borda sutil, focus ring violet
 *  - glass   — vidro com backdrop-blur (pra topbar, dialogs)
 *  - otp     — large monoespaçada com tracking pra códigos OTP
 *
 * Sizes: sm · md · lg
 */
const inputVariants = cva(
  [
    'w-full text-ink placeholder:text-ink-faint',
    'rounded-2xl border transition-colors',
    'outline-none focus-visible:ring-2 focus-visible:ring-violet/40',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'aria-invalid:border-error aria-invalid:ring-error/30',
  ],
  {
    variants: {
      variant: {
        default: ['bg-bg/60 border-border-subtle', 'focus-visible:border-violet/60'],
        glass: [
          'bg-surface/60 backdrop-blur-xl border-border-subtle',
          'focus-visible:border-violet/60 focus-visible:bg-surface',
        ],
        otp: [
          'bg-bg/60 border-border-subtle',
          'text-center font-mono text-2xl tracking-[0.5em]',
          'focus-visible:border-violet/60',
        ],
      },
      size: {
        sm: 'h-9 px-3 text-xs',
        md: 'h-11 px-4 text-sm',
        lg: 'h-12 px-5 text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export type InputVariant = NonNullable<VariantProps<typeof inputVariants>['variant']>;

// `size` em HTMLInput colide com o prop CSS — omitimos e expomos só via cva.
export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, VariantProps<typeof inputVariants> {}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, variant, size, type, ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      type={type}
      className={cn(inputVariants({ variant, size }), className)}
      {...props}
    />
  );
});

export { inputVariants };
